<template>
    <q-btn
        class="q-my-sm row full-width q-py-none"
        text-color="secondary"
        color="white"
        outline
        @click="handleClick"
    >
        <img :src="icon" class="logo col-2" v-if="icon" />
        <span class="text-center col-10 text-black">
            {{
                $t('Login with {providerName}', {
                    providerName: providerName
                })
            }}
        </span>
        <q-badge floating rounded class="badge text-bold text-uppercase">{{
            $t('Try me')
        }}</q-badge>
    </q-btn>
</template>

<script>
import { SSO_PROVIDER_BUTTON_STYLES } from '@models/ssoProviders.js';
import { mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'SSOButton',
    props: {
        providerType: {
            type: String,
            required: true
        },
        authURL: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            codeChallengeMethod: 'S256',

            CODE_VERIFIER_ALLOWED_CHARS:
                'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
            CODE_VERIFIER_LENGTH: 128
        };
    },
    methods: {
        ...mapActions(useAuthStore, [
            'setCodeVerifierAction',
            'setProviderTypeAction'
        ]),
        /**
         * Handle click event
         */
        async handleClick() {
            const codeVerifier = this.generateCodeVerifier();
            const codeChallenge =
                await this.generateCodeChallenge(codeVerifier);

            this.setProviderTypeAction(this.providerType);
            this.setCodeVerifierAction(codeVerifier);

            const url = new URL(this.authURL);
            url.searchParams.set('code_challenge', codeChallenge);
            url.searchParams.set(
                'code_challenge_method',
                this.codeChallengeMethod
            );
            url.searchParams.set('redirect_uri', this.redirectURI);

            if (this.$q.platform.is.capacitor) {
                // Use the state variable to identify the source
                // This is important for handling the redirect after OAuth
                url.searchParams.set('state', 'source_mobile');
                await this.$openUrl({
                    url: url.toString()
                });
            } else {
                window.location.href = url.toString();
            }
        },
        /**
         * Generate a PKCE code verifier
         * @returns {string}
         */
        generateCodeVerifier() {
            let text = '';

            for (let i = 0; i < this.CODE_VERIFIER_LENGTH; i++) {
                text += this.CODE_VERIFIER_ALLOWED_CHARS.charAt(
                    Math.floor(
                        Math.random() * this.CODE_VERIFIER_ALLOWED_CHARS.length
                    )
                );
            }
            return text;
        },
        /**
         * Generate a PKCE code challenge
         * @param {string} codeVerifier
         * @returns {string}
         */
        async generateCodeChallenge(codeVerifier) {
            const digest = await crypto.subtle.digest(
                'SHA-256',
                new TextEncoder().encode(codeVerifier)
            );

            return btoa(String.fromCharCode(...new Uint8Array(digest)))
                .replace(/=/g, '')
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .slice(0, 43);
        }
    },
    computed: {
        /**
         * Get the icon for the provider
         * @returns {string}
         */
        icon() {
            return SSO_PROVIDER_BUTTON_STYLES[this.providerType].icon;
        },
        /**
         * Get the provider name
         * @returns {string}
         */
        providerName() {
            return SSO_PROVIDER_BUTTON_STYLES[this.providerType].label;
        },
        /**
         * Get the redirect URI. This URI will  have to be registered in the identity provider's
         * application settings.
         * @returns {string}
         */
        redirectURI() {
            // If in a mobile environment, build the redirect URI using the tenant
            if (this.$q.platform.is.capacitor) {
                const tenant =
                    this.$pinia.state.value.auth?.tenant?.toLowerCase();

                const tenantURL = `https://${tenant}.iotorq-lean.com`;
                return `${tenantURL}/oauth/complete/`;
            }

            return `${window.location.origin}/oauth/complete/`;
        }
    }
};
</script>

<style lang="scss" scoped>
.logo {
    height: 25px;
    width: 25px;
}

.badge {
    background-color: #b6e5f5;
    color: $primary;
    padding: 5px 10px;
    font-weight: bold;
}
</style>
